import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { DataWrapper } from "../../../../common/components/dataWrapper";
import { KeyValueContainer } from "../../../../common/components/key-value-wrapper";
import Tab from "../../../../common/components/Tab/Tab";
import { COMPANIES } from "../../../../common/constants";
import { KeyValueObj } from "../../../../common/types";
import useAxiosCall from "../../../../hooks/useAxiosCall";
import { AdminActions } from "../components/admin-actions";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import { OnboardingSubmission } from "./subs/onboarding-submission";
import { Modal } from "@/common/components/modal";
import { FileViewer } from "@/common/components/FileViewer";
import { Button } from "@/common/components/button";
import { IoIosEye } from "react-icons/io";
import { FiArrowLeftCircle, FiArrowRightCircle } from "react-icons/fi";
import { ModalWrapper } from "@/common/elements";

const CompanyDetailsWrapper = styled.div`
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.screen.mobile}) {
    padding: 2rem 0.5rem;
  }

  .inner {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .tabs-views {
    background-color: ${({ theme }) => theme.bg.prim_500};
    padding: 4rem 2rem;
  }

  .documents-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    padding: 1rem 0;
  }

  .document-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 1rem;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.bg.prim_400};
  }

  .document-title {
    font-size: 0.9rem;
    font-weight: 500;
    color: ${({ theme }) => theme.text.prim_100};
    text-transform: capitalize;
  }
`;

const ModalNavArrow = styled.button<{ $side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === "left" ? "left: 0.5rem;" : "right: 0.5rem;")}
  transform: translateY(-50%);
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 2rem;
  opacity: 0.6;
  color: ${({ theme }) => theme.text.prim_100};

  :hover {
    opacity: 1;
    color: ${({ theme }) => theme.text.secondary};
  }
`;

export interface IComapanyDetailsProps {}

export function ComapanyDetails(_: IComapanyDetailsProps) {
  const [companyInfo, setCompanyInfo] = useState<KeyValueObj[]>([]);
  const [contactInfo, setContactInfo] = useState<KeyValueObj[]>([]);
  const [, setDocuments] = useState<KeyValueObj[]>([]);
  const [docs, setDocs] = useState<KeyValueObj[]>([]);
  const [currentTab, setCurrentTab] = useState("Company Information");
  const [status, setStatus] = useState<string>("");
  const [retry, setRetry] = useState(0);
  const [selectedDocIndex, setSelectedDocIndex] = useState<number | null>(
    null
  );

  const viewableDocs = docs.filter((doc) => doc.fileUrl);
  const selectedDoc =
    selectedDocIndex !== null ? viewableDocs[selectedDocIndex] : null;

  function handleViewDocument(index: number) {
    setSelectedDocIndex(index);
  }

  function handlePrevDocument() {
    setSelectedDocIndex((prev) =>
      prev === null
        ? prev
        : (prev - 1 + viewableDocs.length) % viewableDocs.length
    );
  }

  function handleNextDocument() {
    setSelectedDocIndex((prev) =>
      prev === null ? prev : (prev + 1) % viewableDocs.length
    );
  }

  const { id, type } = useParams();
  const [fetchCompany, fetchedCompany, companyError, companyLoading] =
    useAxiosCall();
  const [fetchDocument, fetchedDocument, documentError, documentLoading] = useAxiosCall();
  const [fetchContact, fetchedContact, contactError, contactLoading] = useAxiosCall();

  useEffect(() => {
    fetchCompany({
      url: COMPANIES + "/" + id,
      method: "GET",
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, retry]);

  useEffect(() => {
    if (!id) return;
    fetchDocument({
      url: COMPANIES + "/" + id + "/documents",
      method: "GET",
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchContact({
      url: COMPANIES + "/" + id + "/contact",
      method: "GET",
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ====== Document Information ======
  useEffect(() => {
    const innerDocs: KeyValueObj[] = [];

    fetchedDocument &&
      Object.keys(fetchedDocument).forEach((objKey: string) => {
        let url,
          contentType,
          type = "";
        switch (objKey) {
          case "certificate_of_incorporation":
            contentType = fetchedDocument[objKey]?.content_type;
            url = fetchedDocument[objKey]?.url;
            type = contentType === "application/pdf" ? "pdf" : "image";
            if (fetchedDocument[objKey])
              innerDocs.push({
                title: "Certificate of Incorporation",
                fileUrl: url,
                type,
              });
            break;

          case "nafdac_registration_certificate":
            contentType = fetchedDocument[objKey]?.content_type;
            url = fetchedDocument[objKey]?.url;
            type = contentType === "application/pdf" ? "pdf" : "image";
            if (fetchedDocument[objKey])
              innerDocs.push({
                title: "NAFDAC registeration certificate",
                fileUrl: url,
                type,
              });
            break;

          case "tax_clearance":
            contentType = fetchedDocument[objKey]?.content_type;
            url = fetchedDocument[objKey]?.url;
            type = contentType === "application/pdf" ? "pdf" : "image";
            if (fetchedDocument[objKey])
              innerDocs.push({
                title: "Tax Clearance",
                fileUrl: url,
                type,
              });
            break;

          case "company_logo":
            contentType = fetchedDocument[objKey]?.content_type;
            url = fetchedDocument[objKey]?.url;
            type = contentType === "application/pdf" ? "pdf" : "image";
            if (fetchedDocument[objKey])
              innerDocs.push({
                title: "Company Logo",
                fileUrl: url,
                type,
              });
            break;

          default:
            innerDocs.push({
              title: objKey.split("_").join(" "),
              value: fetchedCompany[objKey],
            });
        }
      });
    setDocs(innerDocs);
  }, [fetchedDocument]);

  // ====== Contact Person Information ======
  useEffect(() => {
    const innerContactInfo: KeyValueObj[] = [];

    const contact = fetchedContact;
    Object.keys(contact).length !== 0 &&
      Object.keys(contact).forEach((objKey: string) => {
        switch (objKey) {
          case "id":
          case "created_at":
          case "company_id":
          case "updated_at":
          case "settings":
            break;
          default:
            innerContactInfo.push({
              title: objKey.split("_").join(" "),
              value: contact[objKey],
            });
        }
      });
    setContactInfo(innerContactInfo);
  }, [fetchedContact]);

  // ====== Company Information ======
  useEffect(() => {
    const innerCompanyInfo: KeyValueObj[] = [];
    const innerDocuments: KeyValueObj[] = [];

    fetchedCompany &&
      Object.keys(fetchedCompany).forEach((objKey: string) => {
        let url,
          contentType,
          type = "";

        switch (objKey) {
          case "approval_status":
          case "contact":
          case "id":
          case "users":
          case "api_key":
            break;
            objKey;
          case "certificate_of_incorporation":
            contentType = fetchedCompany[objKey]?.content_type;
            url = fetchedCompany[objKey]?.url;
            type = contentType === "application/pdf" ? "pdf" : "image";
            if (fetchedCompany[objKey])
              innerDocuments.push({
                title: "Certificate of Incorporation",
                fileUrl: url,
                type,
              });
            break;

          case "nafdac_registration_certificate":
            contentType = fetchedCompany[objKey]?.content_type;
            url = fetchedCompany[objKey]?.url;
            type = contentType === "application/pdf" ? "pdf" : "image";
            if (fetchedCompany[objKey])
              innerDocuments.push({
                title: "NAFDAC registeration certificate",
                fileUrl: url,
                type,
              });
            break;

          case "tax_clearance":
            contentType = fetchedCompany[objKey]?.content_type;
            url = fetchedCompany[objKey]?.url;
            type = contentType === "application/pdf" ? "pdf" : "image";
            if (fetchedCompany[objKey])
              innerDocuments.push({
                title: "Tax Clearance",
                fileUrl: url,
                type,
              });
            break;

          case "company_logo":
            contentType = fetchedCompany[objKey]?.content_type;
            url = fetchedCompany[objKey]?.url;
            type = contentType === "application/pdf" ? "pdf" : "image";
            if (fetchedCompany[objKey])
              innerDocuments.push({
                title: "Company Logo",
                fileUrl: url,
                type,
              });
            break;

          case "tin":
            innerCompanyInfo.push({
              title: "TIN",
              value: fetchedCompany[objKey],
            });
            break;

          case "rc_number":
            innerCompanyInfo.push({
              title: "RC Number",
              value: fetchedCompany[objKey],
            });
            break;

          default:
            innerCompanyInfo.push({
              title: objKey.split("_").join(" "),
              value: fetchedCompany[objKey],
            });
        }
      });

    setCompanyInfo(innerCompanyInfo);
    setDocuments(innerDocuments);
    setStatus(fetchedCompany.approval_status);
  }, [fetchedCompany]);

  const tabMap = useMemo(
    () => ({
      companyInfo: "Company Information",
      contactInfo: "Contact Person",
      uploadedDocument: "Documents Uploaded",
    }),
    []
  );

  function handleLeftArrow() {
    switch (currentTab) {
      case tabMap.companyInfo:
        setCurrentTab(tabMap.uploadedDocument);
        break;

      case tabMap.contactInfo:
        setCurrentTab(tabMap.companyInfo);
        break;

      case tabMap.uploadedDocument:
        setCurrentTab(tabMap.contactInfo);
        break;

      default:
        break;
    }
  }

  function handleRightArrow() {
    switch (currentTab) {
      case tabMap.companyInfo:
        setCurrentTab(tabMap.contactInfo);
        break;

      case tabMap.contactInfo:
        setCurrentTab(tabMap.uploadedDocument);
        break;

      case tabMap.uploadedDocument:
        setCurrentTab(tabMap.companyInfo);
        break;

      default:
        break;
    }
  }

  return (
    <CompanyDetailsWrapper>
      <PageTitleNavs
        title="View Company"
        backLink={"/dashboard/admin/companies/" + type}
        bcrumbs={[
          { title: "Companies", link: "/dashboard/admin/companies/" + type },
          { title: "View", link: "#" },
        ]}
      />
      <DataWrapper
        data={fetchedCompany}
        noDataMessage={"No company information to show"}
        loading={companyLoading}
        error={companyError}
        loadingMessage="Loading company data..."
        failedMessage="Could not fetch company data. Check your network and click the button below to retry"
        onFailAction={() => {
          setRetry((prev) => prev + 1);
        }}
      >
        <div className="inner">
          <Tab
            tabsTitle={[
              tabMap.companyInfo,
              tabMap.contactInfo,
              tabMap.uploadedDocument,
            ]}
            onTabClick={(title: string) => setCurrentTab(title)}
            currentTab={currentTab}
          />
          <div className="tabs-views">
            {currentTab === tabMap.companyInfo && (
              <>
                {/* MED-671 (AC4): the submission the decision is being made
                    about, stated in full and in order, above the generic field
                    listing that was already here. */}
                <OnboardingSubmission company={fetchedCompany} />
                <KeyValueContainer data={companyInfo} />
              </>
            )}

            {currentTab === tabMap.contactInfo && (
              <DataWrapper
                data={contactInfo}
                loading={contactLoading}
                error={contactError}
                noDataMessage="No contact person on record for this company"
                failedMessage="Could not fetch contact information. Check your network and click the button below to retry"
                onFailAction={() =>
                  fetchContact({
                    url: COMPANIES + "/" + id + "/contact",
                    method: "GET",
                  })
                }
              >
                <KeyValueContainer data={contactInfo} />
              </DataWrapper>
            )}
            {currentTab === tabMap.uploadedDocument && (
              <DataWrapper
                data={docs}
                loading={documentLoading}
                error={documentError}
                noDataMessage="No documents uploaded for this company"
                failedMessage="Could not fetch documents. Check your network and click the button below to retry"
                onFailAction={() =>
                  fetchDocument({
                    url: COMPANIES + "/" + id + "/documents",
                    method: "GET",
                  })
                }
              >
                <div className="documents-list">
                  {viewableDocs.map((doc, i) => (
                    <div className="document-row" key={i}>
                      <h3 className="document-title">{doc.title}</h3>
                      <Button secondary onClick={() => handleViewDocument(i)}>
                        View <IoIosEye style={{ fontSize: "1.5rem" }} />
                      </Button>
                    </div>
                  ))}
                </div>
              </DataWrapper>
            )}
          </div>

          <Modal
            title={selectedDoc?.title}
            subTitle={
              selectedDocIndex !== null && viewableDocs.length > 1
                ? `${selectedDocIndex + 1} of ${viewableDocs.length}`
                : undefined
            }
            isOpen={!!selectedDoc}
            close={() => setSelectedDocIndex(null)}
          >
            <ModalWrapper>
              {viewableDocs.length > 1 && (
                <>
                  <ModalNavArrow
                    type="button"
                    $side="left"
                    title="Previous document"
                    onClick={handlePrevDocument}
                  >
                    <FiArrowLeftCircle />
                  </ModalNavArrow>
                  <ModalNavArrow
                    type="button"
                    $side="right"
                    title="Next document"
                    onClick={handleNextDocument}
                  >
                    <FiArrowRightCircle />
                  </ModalNavArrow>
                </>
              )}
              <FileViewer
                fileUrl={selectedDoc?.fileUrl}
                fileType={selectedDoc?.type}
                pdfHeight="80vh"
              />
            </ModalWrapper>
          </Modal>

          <AdminActions
            postUrl={COMPANIES + "/approval/" + id}
            initialStatus={status}
            showArrows
            onArrowLeftClick={handleLeftArrow}
            onArrowRightClick={handleRightArrow}
          />
        </div>
      </DataWrapper>
    </CompanyDetailsWrapper>
  );
}
